/**
 * WONE-568: OAuth 2.1 + PKCE token substrate for the admin portal.
 *
 * Mirrors the learner-web auth-tokens module
 * (frontend/web/src/providers/auth-tokens.ts) but trimmed:
 *   - no OTP (admins don't sign up via OTP)
 *   - no unified email-first method resolution (admin is single-method)
 *   - JS (admin's bundler is CRA on Node 16 / React 16.8, no TS)
 *
 * Storage model:
 *   - Access token kept in module-local memory (XSS-resistant — never
 *     touches localStorage). Lost on tab close; refresh round-trip
 *     rehydrates on bootstrap.
 *   - Refresh token lives in the ``__Secure-wone_refresh`` HttpOnly
 *     cookie set by Platform on the api.* origin. Browser handles
 *     attachment via ``credentials: "include"``.
 *   - A single ``Authed`` flag in localStorage signals "this device has
 *     a refresh cookie" so the auth gate can pick the right initial
 *     view without round-tripping. Cleared on logout / refresh failure.
 */

export const PLATFORM_ORIGIN = (
  process.env.REACT_APP_PLATFORM_ORIGIN ||
  process.env.REACT_APP_FILE_UPLOAD_URL ||
  'http://127.0.0.1:8000/'
).replace(/\/+$/, '');

const CLIENT_ID = 'wone-admin';
const SSO_SCOPES = 'openid profile email';
const REDIRECT_PATH = '/auth/callback';
const PKCE_SESSION_KEY = 'wone.admin.pkce';
const AUTHED_FLAG_KEY = 'Authed';

let accessToken = null;

/**
 * Thrown when we genuinely could not verify the session (network / 5xx),
 * as opposed to a definitive "not signed in" / "not an admin". Callers
 * use this to keep the token and surface a "try again" rather than
 * discarding the session and claiming the user lacks access.
 */
export class VerifyError extends Error {
  constructor(message = 'could not verify session') {
    super(message);
    this.name = 'VerifyError';
    this.code = 'verify_failed';
  }
}

export function getAccessToken() {
  return accessToken;
}

export function setAccessToken(token) {
  accessToken = token;
  try {
    if (token) {
      localStorage.setItem(AUTHED_FLAG_KEY, '1');
    } else {
      localStorage.removeItem(AUTHED_FLAG_KEY);
    }
  } catch (err) {
    // Storage may be disabled (private mode Safari, locked-down org
    // policies). Auth still works without the hint — bootstrap will
    // attempt a refresh round-trip either way.
  }
}

export function clearAccessToken() {
  setAccessToken(null);
}

function getRedirectUri() {
  return `${window.location.origin}${REDIRECT_PATH}`;
}

async function postForm(path, body) {
  const params = new URLSearchParams(body);
  return fetch(`${PLATFORM_ORIGIN}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    credentials: 'include',
    body: params.toString(),
  });
}

async function postJson(path, body) {
  return fetch(`${PLATFORM_ORIGIN}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

// Single-flight guard: the backend ROTATES the refresh token on every
// /refresh call and revokes the whole family on reuse. If several
// requests 401 at once (e.g. the dashboard fires parallel queries
// alongside the bootstrap user lookup) and each calls refresh, the
// concurrent calls present the same cookie, the server rotates once and
// treats the rest as replay → the whole session is revoked. Dedupe all
// concurrent callers onto one in-flight refresh.
let _inflightRefresh = null;

async function _doRefresh() {
  try {
    const res = await postJson('/api/v1/auth/refresh', { client_id: CLIENT_ID });
    if (!res.ok) {
      // A 5xx here is a Platform outage, not an expired session — but we
      // can't safely keep a token we couldn't refresh. Log it so a
      // refresh-endpoint outage that silently logs everyone out leaves a
      // breadcrumb (no Sentry in this app; console is the only signal).
      if (res.status >= 500) {
        // eslint-disable-next-line no-console
        console.error(`[auth] refresh failed: HTTP ${res.status}`);
      }
      clearAccessToken();
      return null;
    }
    const data = await res.json();
    if (!data || !data.access_token) {
      // eslint-disable-next-line no-console
      console.error('[auth] refresh succeeded but response had no access_token');
      clearAccessToken();
      return null;
    }
    setAccessToken(data.access_token);
    return data.access_token;
  } catch (err) {
    // CORS error / offline / DNS — clear the hint so the gate falls
    // through to the sign-in screen instead of looping.
    // eslint-disable-next-line no-console
    console.error('[auth] refresh request failed', err);
    clearAccessToken();
    return null;
  }
}

/**
 * Exchange the HttpOnly refresh cookie for a fresh access token.
 * Returns the new access token on success, or null when no valid
 * session exists / the network call fails. Never throws — defensive
 * by design so the bootstrap path can't wedge the auth gate.
 *
 * Concurrent callers share a single in-flight request (see above).
 */
export function refreshAccessToken() {
  if (_inflightRefresh) return _inflightRefresh;
  _inflightRefresh = _doRefresh().finally(() => {
    _inflightRefresh = null;
  });
  return _inflightRefresh;
}

// GraphQL error messages that mean "the access token is missing/expired,
// a refresh may fix it" rather than "the token is valid but this user
// lacks access" (not refreshable — see isRefreshableAuthError callers).
// "Signature has expired" / "Error decoding signature" are legacy
// graphql-jwt (HS256); "Authentication required" is the WONE-556
// strict-mode (OAuth) equivalent.
const REFRESHABLE_AUTH_ERROR_SUBSTRINGS = [
  'Signature has expired',
  'Error decoding signature',
  'Authentication required',
];

/**
 * True if a GraphQL error message indicates a missing/expired token
 * (refresh-and-retry may fix it), as opposed to a valid token that
 * lacks permission (retrying would just loop).
 */
export function isRefreshableAuthError(message) {
  const msg = message || '';
  return REFRESHABLE_AUTH_ERROR_SUBSTRINGS.some((substr) => msg.includes(substr));
}

/**
 * Called once on app boot. If the ``Authed`` hint is set, attempt a
 * refresh; otherwise skip the round-trip and present the sign-in
 * screen. Either way returns the access token (or null).
 */
export async function bootstrapSession() {
  let hadHint = false;
  try {
    hadHint = localStorage.getItem(AUTHED_FLAG_KEY) === '1';
  } catch (err) {
    /* see setAccessToken — storage may be disabled */
  }
  if (!hadHint) return null;
  return refreshAccessToken();
}

/**
 * Begin login via Platform-hosted auth screens (reports pattern,
 * WONE-770). Generates a fresh PKCE pair + state, stashes the verifier
 * in sessionStorage (per-tab; survives the redirect), and full-page-
 * redirects to Platform's /o/authorize. Platform renders the login UI
 * (password + SSO) centrally — the admin has no login form of its own.
 *
 * ``idpHint`` is optional: omit it to land on Platform's full hosted
 * login page; pass e.g. "google-oauth2" to jump straight to that IdP.
 * The round-trip returns to /auth/callback with ?code=...&state=...
 */
export async function beginLogin(idpHint, returnTo) {
  const codeVerifier = randomUrlSafe(32);
  const state = randomUrlSafe(16);
  const codeChallenge = await sha256B64Url(codeVerifier);

  const pkce = {
    code_verifier: codeVerifier,
    state,
    return_to: returnTo || null,
  };
  sessionStorage.setItem(PKCE_SESSION_KEY, JSON.stringify(pkce));

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    redirect_uri: getRedirectUri(),
    scope: SSO_SCOPES,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  // Only pin a provider when explicitly asked; otherwise Platform shows
  // its hosted login page with all available methods.
  if (idpHint) {
    params.set('idp_hint', idpHint);
  }
  window.location.assign(`${PLATFORM_ORIGIN}/o/authorize/?${params.toString()}`);
}

/**
 * Called from /auth/callback after the IdP redirects back. Reads the
 * PKCE verifier from sessionStorage, exchanges the code for tokens via
 * /o/token, and stores the access token in memory. Returns the
 * captured ``return_to`` URL (or null) so the caller can dispatch.
 */
export async function completeSSO(code, state) {
  const raw = sessionStorage.getItem(PKCE_SESSION_KEY);
  sessionStorage.removeItem(PKCE_SESSION_KEY);
  if (!raw) {
    throw new Error('missing PKCE state — possible XSRF or stale link');
  }
  let pkce;
  try {
    pkce = JSON.parse(raw);
  } catch (err) {
    throw new Error('corrupt PKCE state');
  }
  if (pkce.state !== state) {
    throw new Error('state mismatch — possible XSRF');
  }

  const res = await postForm('/o/token/', {
    grant_type: 'authorization_code',
    client_id: CLIENT_ID,
    code,
    code_verifier: pkce.code_verifier,
    redirect_uri: getRedirectUri(),
  });
  if (!res.ok) {
    // Surface the OAuth error body (RFC 6749 §5.2). invalid_grant
    // (expired/replayed code) and a misconfigured redirect_uri render
    // very differently for an operator — collapsing both to a generic
    // message hides config errors that would otherwise loop forever.
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body && body.error) {
        detail = body.error_description
          ? `${body.error}: ${body.error_description}`
          : body.error;
      }
    } catch (err) {
      /* non-JSON error body — keep the status */
    }
    throw new Error(`token exchange failed (${detail})`);
  }
  const data = await res.json();
  if (!data || !data.access_token) {
    // Mirror _doRefresh's guard: a 200 with a malformed body is a token
    // error, not a sign-in/role failure — don't let it masquerade as one.
    throw new Error('token exchange returned no access_token');
  }
  setAccessToken(data.access_token);
  return pkce.return_to;
}

/**
 * GET /api/v1/auth/me — returns the authenticated user's identity.
 *
 * Returns null only when there's genuinely no session (no token, or
 * 401/403). Throws ``VerifyError`` on a transient failure (network /
 * 5xx) so callers can distinguish "not signed in" from "couldn't
 * verify" and avoid telling a real admin they're not one.
 */
export async function fetchMe() {
  const token = getAccessToken();
  if (!token) return null;
  let res;
  try {
    res = await fetch(`${PLATFORM_ORIGIN}/api/v1/auth/me`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[auth] /me request failed (transient)', err);
    throw new VerifyError();
  }
  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) {
    // eslint-disable-next-line no-console
    console.error(`[auth] /me failed: HTTP ${res.status}`);
    throw new VerifyError();
  }
  return res.json();
}

/**
 * POST /api/v1/auth/revoke. Server clears the refresh cookie; we
 * clear in-memory access + the Authed hint + legacy localStorage keys
 * left over from the pre-OAuth admin (graceful upgrade — see WONE-567).
 */
export async function revokeSession() {
  // Clear local state FIRST and synchronously — this drops the in-memory
  // access token + the ``Authed`` hint + legacy keys before any await, so
  // the auth gate flips to logged-out immediately and a caller that
  // doesn't await us can't race a stale ``Authed`` flag back into the
  // dashboard. The revoke POST below uses the HttpOnly refresh cookie,
  // not the access token, so clearing first doesn't break it.
  clearAccessToken();
  try {
    localStorage.removeItem('WonEToken');
    localStorage.removeItem('WonEUser');
  } catch (err) {
    /* storage disabled */
  }

  // Then revoke server-side. A failure here does NOT throw (logout must
  // still complete locally), but it IS logged — a failed revoke means the
  // refresh-cookie session is still live server-side, which matters for a
  // stolen-device scenario. (No Sentry in this app; console is the signal.)
  try {
    const res = await postJson('/api/v1/auth/revoke', { client_id: CLIENT_ID });
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.error(`[auth] revoke failed: HTTP ${res.status} — server session may still be active`);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[auth] revoke request failed — server session may still be active', err);
  }
}

// --- PKCE helpers (Web Crypto; admin's browserslist excludes IE) ---

function randomUrlSafe(byteLength) {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256B64Url(input) {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
